import React, { Component } from 'react'
import { withRouter } from 'react-router'
import RestApiUtils from '../../apiclient/moebli/RestApiUtils'
import NachClientResponsePageUI from './NachClientResponsePageUI'

const isExpiredSignal = (text) => {
  if (!text || typeof text !== 'string') return false
  const lower = text.toLowerCase()
  return (
    lower.includes('expired') ||
    lower.includes('validity') ||
    lower.includes('window closed') ||
    lower.includes('link closed')
  )
}

const isAlreadySubmittedSignal = (text) => {
  if (!text || typeof text !== 'string') return false
  const lower = text.toLowerCase()
  const signalWords = ['already', 'taken', 'recorded', 'duplicate']
  const signalPhrases = [
    'has been submitted',
    'previously submitted',
    'already submitted',
    'already confirmed',
    'already taken',
    'already recorded',
    'is taken already',
    'confirmation taken',
    'already received'
  ]
  if (signalWords.some(w => lower.includes(w))) return true
  if (signalPhrases.some(p => lower.includes(p))) return true
  return false
}

class NachClientResponsePageSF extends Component {
  constructor(props) {
    super(props)
    const { match } = props
    const key = match && match.params ? match.params.key : null
    let localSaved = null
    if (key) {
      try {
        const raw = localStorage.getItem(`nach_confirmed_${key}`)
        if (raw) {
          localSaved = JSON.parse(raw)
        }
      } catch (e) {
        // localStorage not available or error parsing
      }
    }

    this.state = {
      loading: !localSaved,
      error: null,
      successMsg: null,
      data: localSaved && localSaved.yearMonth ? { yearMonth: localSaved.yearMonth } : null,
      selectedAnswer: null,
      submitting: false,
      submitted: false,
      submittedAnswer: localSaved ? (localSaved.answer || null) : null,
      alreadySubmitted: Boolean(localSaved),
      alreadySubmittedMsg: localSaved ? (localSaved.msg || 'You have already submitted your confirmation.') : null,
      isExpired: false
    }
  }

  componentDidMount() {
    this.loadPresentationDetails()
  }

  checkExpired = (json, rawErrMsg) => {
    if (isExpiredSignal(rawErrMsg)) return true
    if (!json) return false

    const target = json.nachMonthlyDetails || json.data || json
    const status = typeof target.status === 'string' ? target.status.toLowerCase() : ''
    if (status === 'expired' || target.expired || json.expired || target.isExpired || json.isExpired) {
      return true
    }
    const msg = target.message || json.message || target.error || json.error
    return isExpiredSignal(msg)
  }

  checkIfAlreadySubmitted = (json, rawErrMsg) => {
    if (isAlreadySubmittedSignal(rawErrMsg)) {
      return { isSubmitted: true, message: rawErrMsg }
    }
    if (!json) return { isSubmitted: false }

    const target = json.nachMonthlyDetails || json.data || json

    // Check status string
    const status = typeof target.status === 'string' ? target.status.toLowerCase() : ''
    if (['already_submitted', 'submitted', 'confirmed', 'completed', 'recorded', 'yes', 'no'].includes(status)) {
      return {
        isSubmitted: true,
        clientResponse: (status === 'yes' || status === 'no') ? status : null,
        message: target.message || json.message
      }
    }

    // Check clientResponse / response
    const clientResponse = typeof target.clientResponse === 'string'
      ? target.clientResponse.toLowerCase()
      : (typeof json.clientResponse === 'string' ? json.clientResponse.toLowerCase() : '')
    if (clientResponse === 'yes' || clientResponse === 'no') {
      return { isSubmitted: true, clientResponse, message: target.message || json.message }
    }

    const response = typeof target.response === 'string'
      ? target.response.toLowerCase()
      : (typeof json.response === 'string' ? json.response.toLowerCase() : '')
    if (response === 'yes' || response === 'no') {
      return { isSubmitted: true, clientResponse: response, message: target.message || json.message }
    }

    // Check boolean flags
    if (target.alreadySubmitted || json.alreadySubmitted || target.isSubmitted || json.isSubmitted || target.submitted || json.submitted) {
      return { isSubmitted: true, message: target.message || json.message }
    }

    // Check message / successMsg / remark
    const msg = target.message || json.message || target.successMsg || json.successMsg || target.remark || json.remark
    if (isAlreadySubmittedSignal(msg)) {
      return { isSubmitted: true, message: msg }
    }

    // Check errorList items
    if (Array.isArray(json.errorList)) {
      for (const err of json.errorList) {
        const errText = typeof err === 'string' ? err : (err && err.message ? err.message : '')
        if (isAlreadySubmittedSignal(errText)) {
          return { isSubmitted: true, message: errText }
        }
      }
    }

    return { isSubmitted: false }
  }

  loadPresentationDetails = () => {
    const { match, context } = this.props
    const key = match && match.params ? match.params.key : null
    const hash = match && match.params ? match.params.hash : null

    if (!key || !hash) {
      this.setState({
        loading: false,
        error: 'Invalid or missing link parameters.'
      })
      return
    }

    const isDemo = key === 'demo' || (window.location && window.location.search.includes('mock=true'))

    if (isDemo) {
      if (!this.state.alreadySubmitted) {
        setTimeout(() => {
          this.setState({
            loading: false,
            data: { status: 'pending' },
            error: null
          })
        }, 400)
      } else {
        this.setState({ loading: false })
      }
      return
    }

    const baseUrl = RestApiUtils.getServerUrl(context)
    const requestUrl = `${baseUrl}/api/direct/nach/monthly/response?key=${encodeURIComponent(key)}&hash=${encodeURIComponent(hash)}`

    fetch(requestUrl, {
      method: 'GET',
      headers: {
        Accept: 'application/json'
      }
    })
      .then((response) => {
        if (response.status === 204 || response.status === 201 || response.status === 202) {
          return { success: true, errorList: [] }
        }
        return response.text().then(text => {
          try {
            return text ? JSON.parse(text) : { success: response.ok, errorList: [] }
          } catch (e) {
            return { success: response.ok, errorList: response.ok ? [] : [{ message: text }] }
          }
        })
      })
      .then((json) => {
        const rawErrors = json.errorList || []
        const rawErrMsg = rawErrors.length > 0
          ? (typeof rawErrors[0] === 'string' ? rawErrors[0] : (rawErrors[0].message || String(rawErrors[0])))
          : (json.message || json.error || null)

        if (this.checkExpired(json, rawErrMsg)) {
          this.setState({
            loading: false,
            isExpired: true,
            alreadySubmitted: false,
            error: null,
            data: { status: 'expired', message: rawErrMsg || json.message || 'The confirmation window has closed.' }
          })
          return
        }

        const alreadyCheck = this.checkIfAlreadySubmitted(json, rawErrMsg)
        if (alreadyCheck.isSubmitted || this.state.alreadySubmitted) {
          const finalMsg = alreadyCheck.message || rawErrMsg || this.state.alreadySubmittedMsg || 'You have already submitted your confirmation.'
          const finalAnswer = alreadyCheck.clientResponse || this.state.submittedAnswer || null
          const yearMonth = (json.nachMonthlyDetails && json.nachMonthlyDetails.yearMonth) || json.yearMonth || (this.state.data && this.state.data.yearMonth) || null

          try {
            localStorage.setItem(`nach_confirmed_${key}`, JSON.stringify({
              answer: finalAnswer,
              time: Date.now(),
              msg: finalMsg,
              yearMonth: yearMonth
            }))
          } catch (e) {}

          this.setState({
            loading: false,
            alreadySubmitted: true,
            alreadySubmittedMsg: finalMsg,
            submittedAnswer: finalAnswer,
            data: json.nachMonthlyDetails || json || (yearMonth ? { yearMonth } : null),
            error: null
          })
          return
        }

        const isError = rawErrors.length > 0 || json.success === false || !!json.error
        if (isError) {
          this.setState({
            loading: false,
            error: rawErrMsg || 'Failed to verify NACH presentation link.',
            data: null
          })
        } else {
          this.setState({
            loading: false,
            data: json.nachMonthlyDetails || json,
            successMsg: json.successMsg || json.message || null,
            error: null
          })
        }
      })
      .catch((err) => {
        console.error('Failed to load NACH presentation details:', err)
        if (!this.state.alreadySubmitted) {
          this.setState({
            loading: false,
            error: 'Network error while verifying link. Please check your connection and try again.'
          })
        } else {
          this.setState({ loading: false })
        }
      })
  }

  handleAnswer = (answer) => {
    if (this.state.submitting || this.state.submitted || this.state.alreadySubmitted) return

    const { match, context } = this.props
    const key = match && match.params ? match.params.key : null
    const hash = match && match.params ? match.params.hash : null

    const isDemo = key === 'demo' || (window.location && window.location.search.includes('mock=true'))
    if (isDemo) {
      this.setState({ submitting: true, error: null, selectedAnswer: answer })
      setTimeout(() => {
        const okMsg = 'Thank you. Your confirmation has been submitted successfully.'
        try {
          localStorage.setItem(`nach_confirmed_${key}`, JSON.stringify({
            answer: answer,
            time: Date.now(),
            msg: okMsg,
            yearMonth: this.state.data && this.state.data.yearMonth
          }))
        } catch (e) {}
        this.setState({
          submitting: false,
          submitted: true,
          alreadySubmitted: true,
          submittedAnswer: answer,
          successMsg: okMsg
        })
      }, 500)
      return
    }

    this.setState({ submitting: true, error: null, selectedAnswer: answer })

    const baseUrl = RestApiUtils.getServerUrl(context)
    const postUrl = `${baseUrl}/api/direct/nach/monthly/response`

    const payload = {
      answer: answer,
      key: key,
      hash: hash
    }

    fetch(postUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    })
      .then((response) => {
        if (response.status === 204 || response.status === 201 || response.status === 202) {
          return { success: true, errorList: [] }
        }
        return response.text().then(text => {
          try {
            return text ? JSON.parse(text) : { success: response.ok, errorList: [] }
          } catch (e) {
            return { success: response.ok, errorList: response.ok ? [] : [{ message: text }] }
          }
        })
      })
      .then((json) => {
        const rawErrors = json.errorList || []
        const rawErrMsg = rawErrors.length > 0
          ? (typeof rawErrors[0] === 'string' ? rawErrors[0] : (rawErrors[0].message || String(rawErrors[0])))
          : (json.message || json.error || null)

        if (this.checkExpired(json, rawErrMsg)) {
          this.setState({
            submitting: false,
            isExpired: true,
            error: null,
            data: { status: 'expired', message: rawErrMsg || json.message || 'The confirmation window has closed.' }
          })
          return
        }

        const alreadyCheck = this.checkIfAlreadySubmitted(json, rawErrMsg)
        if (alreadyCheck.isSubmitted) {
          const finalMsg = alreadyCheck.message || rawErrMsg || 'Your confirmation has already been recorded.'
          try {
            localStorage.setItem(`nach_confirmed_${key}`, JSON.stringify({
              answer: answer,
              time: Date.now(),
              msg: finalMsg,
              yearMonth: this.state.data && this.state.data.yearMonth
            }))
          } catch (e) {}
          this.setState({
            submitting: false,
            submitted: true,
            alreadySubmitted: true,
            submittedAnswer: answer,
            alreadySubmittedMsg: finalMsg,
            successMsg: finalMsg,
            error: null
          })
          return
        }

        const isError = rawErrors.length > 0 || json.success === false || !!json.error
        if (isError) {
          this.setState({
            submitting: false,
            error: rawErrMsg || 'Failed to submit your confirmation. Please try again.'
          })
        } else {
          const okMsg = json.successMsg || json.message || 'Thank you. Your confirmation has been submitted successfully.'
          try {
            localStorage.setItem(`nach_confirmed_${key}`, JSON.stringify({
              answer: answer,
              time: Date.now(),
              msg: okMsg,
              yearMonth: this.state.data && this.state.data.yearMonth
            }))
          } catch (e) {}
          this.setState({
            submitting: false,
            submitted: true,
            alreadySubmitted: true,
            submittedAnswer: answer,
            successMsg: okMsg,
            error: null
          })
        }
      })
      .catch((err) => {
        console.error('Failed to submit NACH confirmation:', err)
        this.setState({
          submitting: false,
          error: 'Network error while submitting confirmation. Please try again.'
        })
      })
  }

  render() {
    return (
      <NachClientResponsePageUI
        loading={this.state.loading}
        error={this.state.error}
        successMsg={this.state.successMsg}
        data={this.state.data}
        submitting={this.state.submitting}
        submitted={this.state.submitted}
        submittedAnswer={this.state.submittedAnswer}
        alreadySubmitted={this.state.alreadySubmitted}
        alreadySubmittedMsg={this.state.alreadySubmittedMsg}
        isExpired={this.state.isExpired}
        onAnswer={this.handleAnswer}
      />
    )
  }
}

export default withRouter(NachClientResponsePageSF)
